import { LocationConfidenceScoresLabelEnum, RecommendedActionTypeEnum, PatientVolumeLabelEnum } from "@app/projects/enumerators";
import { isEqual, startCase } from "lodash";
import { Component } from "react";
import * as helpers from "./exportPanelBodyHelpers";
import * as toast from "./toast";
import { FeatureFlagContextProps, withFeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { ExportProviderAvailabilityType, NetworkProviderFilterType, ExportFormatType, ExportGapFilterType, AccuracySupplementalRowTypes, ReportSettingsType } from "@app/exports/enumerations";
import { AccuracySelectProviderFilter, AccuracySelectTrueCountyFilter, AccuracySelectEntityTypeFilter, AccuracyRecommendedActionFilter, AccuracyRowTypeFilter, SelectFormat } from "@app/exports/export-panel-body/filters";
import AccuracyPcaFilter from "@app/exports/export-panel-body/filters/AccuracyPcaFilter";
import AccuracyPractitionerAtLocationConfidenceFilter from "@app/exports/export-panel-body/filters/AccuracyPractitionerAtLocationConfidenceFilter";
import AccuracySelectComparisonRunConfigurationsFilter from "@app/exports/export-panel-body/filters/AccuracySelectComparisonRunConfigurationsFilter";
import GenerateBody from "@app/exports/export-panel-body/generate-body";
import { ReportSettingsBody, ReportSettingsSaveAction } from "@app/exports/export-panel-body/report-settings";
import { ReportSettingsProps, ReportSettingsState } from "@app/exports/export-panel-body/report-settings/reportSettings";
import { ReportPanelBodyBaseState } from "@app/exports/export-panel-body/reportPanelBodyBase";
import { ReportPanelBodyBaseProps } from "@app/exports/export-panel-body/reportPanelBodyBaseProps";
import { EntityTypeFilter, ExportRecommendedActionReasonModel, RegionSetFilter, ExportAccuracyActionableProvidersModel, ReportSettingsModel } from "@app/exports/exportModels";
import ExportService from "@app/exports/exportService";
import { ProjectModule } from "@app/projects/literals";
import AccuracyFilters from "@app/projects/network-assessment/accuracy/accuracyFilters";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";
import ProjectService from "@app/projects/projectService";
import MultiselectDropdown from "@app/shared/checkbox-dropdown/MultiselectDropdown";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { UserContextProps, withUserContext } from "@app/user/UserContext";

type ActionableProvidersPanelBodyProps =
  ReportPanelBodyBaseProps &
  ReportSettingsProps &
  UserContextProps &
  FeatureFlagContextProps &
  {
    accuracyFilters: AccuracyFilters;
    activeModule: ProjectModule;
    activeNetworkType: ProviderNetworkTypeEnum;
    exportNetworkProviderCount: number;
    inspectionBand: string;
    inspectionUids: string[];
    maxExcelRecords: number;
    nonRecordRows: number;
    networkTypes: ProviderNetworkTypeEnum[];
    providerAvailabilityType: ExportProviderAvailabilityType;
    providerIds: number[];
    specialtyGroupCodes: string[];
    showCountyFirst: boolean;
  };

interface ActionableProvidersPanelBodyState extends ReportPanelBodyBaseState, ReportSettingsState {
  canCompareResults: boolean;
  comparedProjectRunConfigurationId: number;
  entityTypeFilter: EntityTypeFilter;
  excelRecordsLimit: number;
  filterByTrueCounty: boolean;
  isComparingResults: boolean;
  networkProviderFilterType: NetworkProviderFilterType;
  practitionerAtLocationConfidenceIds: number[];
  pcaLabelIds: number[];
  projectRunConfigurations: ProjectRunConfiguration[];
  providerFilter: {
    filterType: ProviderNetworkTypeEnum;
    providerAvailabilityType: ExportProviderAvailabilityType;
  };
  recommendedActionIds: number[];
  recommendedActionReasons: ExportRecommendedActionReasonModel[];
  recommendedActionReasonsFilterDisabled: boolean;
  rowTypeIds: number[];
  regionSetFilter: RegionSetFilter;
  disableRecommendedActionReasonsFilter: boolean;
  selectAllRecommendedActionReasonChecked: boolean;
  selectAllRecommendedActionReasonIndeterminate: boolean;
  selectedRecommendedActionReasons: string[];
  showAllRecommendedActionReasons: boolean;
  showAllRecommendedActionReasonsDisabled: boolean;
  sourceProjectRunConfigurationId: number;
  sharedUsers: number[];
}

export default class ActionableProvidersPanelBody extends Component<ActionableProvidersPanelBodyProps, ActionableProvidersPanelBodyState> {
  private readonly exportService = new ExportService();
  private readonly projectService = new ProjectService();

  constructor(props: ActionableProvidersPanelBodyProps) {
    super(props);

    this.state = {
      ...this.getInitialBodyState(),
      ...this.getInitialReportSettingsState()
    };
  }

  public componentDidUpdate(prevProps: ActionableProvidersPanelBodyProps, prevState: ActionableProvidersPanelBodyState) {
    if (!isEqual(prevProps.reportSettingsList, this.props.reportSettingsList) || !isEqual(prevState.recommendedActionReasons, this.state.recommendedActionReasons)) {
      const reportSettingsList = this.props.reportSettingsList;
      if (reportSettingsList) {
        const currentReportSettings = reportSettingsList.find(rs => rs.isDefault);
        if (currentReportSettings){
          this.handleParseReportSettingsJsonToState(currentReportSettings.reportSettingsJson);
          this.handleSelectedRecommendedActionReasons(currentReportSettings.reportSettingsJson);
        }
        this.setState({
          currentReportSettings,
          reportSettingsList
        })
      }
    }
  }

  public async componentDidMount() {
    const inspectorsForRecommenededActionReasons = this.props.inspectionUids;
    const recommendedActionReasons = await this.exportService.getExportAccuracyRecommendedActionReasons(this.props.projectId, this.props.runConfigurationId, inspectorsForRecommenededActionReasons);
    const selectedRecommendedActionReasons: string[] = [];
    recommendedActionReasons.forEach((r) => selectedRecommendedActionReasons.push(r.reason));

    const project = await this.projectService.getById(this.props.projectId);
    const projectRunConfigurations = project.projectRunConfigurations;

    this.setState((prevState) => ({
      ...prevState,
      projectRunConfigurations,
      recommendedActionReasons,
      selectedRecommendedActionReasons
    }));
  }

  public componentWillUnmount() {
    toast.dismiss();
  }

  public render() {
    const isAccuracyModule = this.props.match.params.module === "accuracy";

    return <>
      {isAccuracyModule &&
        <ReportSettingsBody
          buildReportSettingsModel={this.buildReportSettingsModel}
          createReportSettingsNameInputDisabled={this.state.createReportSettingsNameInputDisabled}
          currentReportSettings={this.state.currentReportSettings}
          isSelectionValid={this.state.isSelectionValid}
          parseReportSettingsJsonToState={this.handleParseReportSettingsJsonToState}
          reportSettingsHasChanges={this.state.reportSettingsHasChanges}
          reportSettingsHasInvalidChanges={this.state.reportSettingsHasInvalidChanges}
          reportSettingsList={this.state.reportSettingsList}
          saveClicked={this.state.saveClicked}
          setCreateReportSettingsNameInputDisabled={this.setCreateReportSettingsNameInputDisabled}
          setCurrentReportSettings={this.setCurrentReportSettings}
          setReportSettingsIsSelectionValid={this.setReportSettingsIsSelectionValid}
          setReportSettingsHasChanges={this.setReportSettingsHasChanges}
          setReportSettingsHasInvalidChanges={this.setReportSettingsHasInvalidChanges}
          setReportSettingsList={this.setReportSettingsList}
          setSaveClicked={this.setSaveClicked}
        />
      }
      <div className="row">
        {this.renderFirstColumnFilters()}
        {this.renderSecondColumnFilters()}
        {this.renderThirdColumnFilters()}
        {this.renderFourthColumnFilters()}
      </div>

      {isAccuracyModule &&
        <ReportSettingsSaveAction
          currentReportSettings={this.state.currentReportSettings}
          isSelectionValid={this.state.isSelectionValid}
          reportSettingsHasChanges={this.state.reportSettingsHasChanges}
          reportSettingsHasInvalidChanges={this.state.reportSettingsHasInvalidChanges}
          reportSettingsList={this.state.reportSettingsList}
          saveClicked={this.state.saveClicked}
          setCurrentReportSettings={this.setCurrentReportSettings}
          setCreateReportSettingsNameInputDisabled={this.setCreateReportSettingsNameInputDisabled}
          setReportSettingsHasChanges={this.setReportSettingsHasChanges}
          setReportSettingsList={this.setReportSettingsList}
          setSaveClicked={this.setSaveClicked}
          stringifyStateReportSettings={this.stringifyStateReportSettings}
        />
      }
      <GenerateBody
        {...this.props}
        emailOnSuccess={this.state.emailOnSuccess}
        reportName={this.state.reportName}
        onGenerateClick={this.handleGenerateClick}
        onEmailOnSuccessChange={this.handleEmailOnSuccessChange}
        onReportNameChange={this.handleReportNameChange}
        isLoading={this.state.loadingReportSettingsJSONStrings && this.state.loadingReportSettingsJSONStrings.indexOf(helpers.stringifyStateReportSettings(this.state)) > -1}
        isValid={(this.state.regionSetFilter.includeActive || this.state.regionSetFilter.includePending || this.state.regionSetFilter.includeApproved)
          && (this.state.entityTypeFilter.includePractitionerEntityType || this.state.entityTypeFilter.includeServiceLocationEntityType)
          && (this.state.rowTypeIds.length > 0)
          && (this.state.recommendedActionIds.length > 0 || this.state.showAllRecommendedActionReasons)
          && (this.state.practitionerAtLocationConfidenceIds.length > 0)
          && (this.state.pcaLabelIds.length > 0) }
        showSharedUsers={true}
        sharedUsers={this.state.sharedUsers}
        updateSelectedSharedUsers={this.handleSharedUsersChange}
        exportType="AccuracyActionableProvider"
        projectId={this.props.projectId}
        networkAssessmentAnalysisType={this.state.regionSetFilter.networkAssessmentAnalysisType}
      />
    </>;
  }

  private getInitialBodyState = (): ActionableProvidersPanelBodyState => {
    return {
      canCompareResults: true,
      comparedProjectRunConfigurationId: null,
      emailOnSuccess: true,
      entityTypeFilter: {
        includePractitionerEntityType: true,
        includeServiceLocationEntityType: true,
      },
      excelRecordsLimit: this.setDefaultExcelRecordsLimit(),
      filterByTrueCounty: true,
      isComparingResults: false,
      loadingReportSettingsJSONStrings: [],
      networkProviderFilterType: NetworkProviderFilterType.Available,
      outputFormat: ExportFormatType.CSV,
      pcaLabelIds: this.setDefaultPcaLabelIds(),
      practitionerAtLocationConfidenceIds: this.setDefaultPractitionerAtLocationConfidenceIds(),
      projectRunConfigurations: [],
      providerFilter: {
        filterType: this.props.activeNetworkType || ProviderNetworkTypeEnum.Network,
        providerAvailabilityType: this.props.comparedRunConfigurationId !== null ? ExportProviderAvailabilityType.Changed : this.props.providerAvailabilityType,
      },
      recommendedActionIds: this.setDefaultRecommendedActionIds(),
      recommendedActionReasons: [],
      recommendedActionReasonsFilterDisabled: this.setDefaultRecommendedActionReasonsFilterDisabled(),
      regionSetFilter: {
        filterType: ExportGapFilterType.All,
        includeActive: true,
        includeApproved: true,
        includePending: true,
        networkAssessmentAnalysisType: this.props.initialNetworkAssessmentAnalysisType,
      },
      reportName: helpers.reportName(this.props.comparedRunConfigurationId !== null ? "ChangedProviders" : startCase(this.props.activeNetworkType || ProviderNetworkTypeEnum.Network)),
      rowTypeIds: this.setDefaultRowTypeIds(),
      selectAllRecommendedActionReasonChecked: true,
      selectAllRecommendedActionReasonIndeterminate: false,
      selectedRecommendedActionReasons: [],
      sharedUsers: [],
      showAllRecommendedActionReasons: this.setDefaultShowAllRecommendedActionReasons(),
      showAllRecommendedActionReasonsDisabled: this.setDefaultShowAllRecommendedActionReasonsDisabled(),
      sourceProjectRunConfigurationId: null,
    } as ActionableProvidersPanelBodyState;
  }

  private getInitialReportSettingsState = (): ReportSettingsState => {
    return {
      createReportSettingsNameInputDisabled: true,
      currentReportSettings: undefined,
      isSelectionValid: true,
      reportSettingsHasChanges: false,
      reportSettingsHasInvalidChanges: false,
      reportSettingsList: [],
      saveClicked: false,
    } as ReportSettingsState;
  }

  private renderFirstColumnFilters() {
    return (
      <div className="col-12 col-sm-3">
        {this.renderSelectProviderFilter()}
        {this.renderSelectTrueCountyFilter()}
      </div>
    );
  }

  private renderSecondColumnFilters() {
    return (
      <div className="col-12 col-sm-3">
        {this.renderRecommendedActionsFilter()}
        {this.renderPracAtLocFilter()}
        {this.renderPcaFilter()}
      </div>
    );
  }

  private renderThirdColumnFilters() {
    return (
      <div className="col-12 col-sm-3">
        {this.renderSelectEntityTypeFilter()}
        {this.renderRowTypesFilter()}
        {this.renderRecommendedActionReasonFilter()}
      </div>
    );
  }

  private renderFourthColumnFilters() {
    return (
      <div className="col-12 col-sm-3">
        {this.renderSelectFormat()}
        {this.renderTimeframeFilter()}
      </div>
    );
  }

  private renderSelectProviderFilter() {
    return (
      <AccuracySelectProviderFilter
        {...this.props}
        {...this.state}
        onProviderFilterTypeChange={this.handleProviderFilterTypeChange}
      />
    );
  }

  private renderSelectTrueCountyFilter() {
    return (
      <AccuracySelectTrueCountyFilter
        exportType="true-county-filter"
        onTrueCountyChange={this.handleTrueCountyChange}
        runConfigurations={this.state.projectRunConfigurations}
      />
    );
  }

  private renderSelectEntityTypeFilter() {
    return (
      <AccuracySelectEntityTypeFilter
        disabled={this.props.disableEntityTypeFilters}
        onEntityTypeIncludeChange={this.handleEntityTypeIncludeChange}
        onEntityTypeIncludeChangeDropdown={this.handleEntityTypeIncludeChangeDropdown}
        entityTypeFilter={this.state.entityTypeFilter}
      />
    );
  }

  private renderRecommendedActionsFilter() {
    return (
      <AccuracyRecommendedActionFilter
        onRecommendedActionChange={this.handleRecommendedActionChange}
        onRecommendedActionChangeDropdown={this.handleRecommendedActionChangeDropdown}
        recommendedActionReasonsFilterDisabled={this.state.showAllRecommendedActionReasonsDisabled}
        recommendedActionIds={this.state.recommendedActionIds}
        onShowAllReasonsChange={this.handleShowAllReasonsChange}
        showAllRecommendedActionReasons={this.state.showAllRecommendedActionReasons}
      />
    );
  }

  private renderPracAtLocFilter() {
    const isExcluded = this.props.accuracyFilters?.excludePracAtLoc;
    return (!isExcluded &&
      <AccuracyPractitionerAtLocationConfidenceFilter
        onPracAtLocConfidenceChange={this.handlePracAtLocConfidenceChange}
        onPracAtLocConfidenceChangeDropdown={this.handlePracAtLocConfidenceChangeDropdown}
        practitionerAtLocationConfidenceIds={this.state.practitionerAtLocationConfidenceIds}
      />
    );
  }

  private renderRecommendedActionReasonFilter() {
    return (
      <>
        <p className="col-12 lead mb0 demibold">Recommended Action Reasons Filter</p>
        <p className="col-12">Please select the actions to include:</p>
        <MultiselectDropdown
          containerClassName="col-12"
          customSelectAllText="All Available Reasons"
          customSelectInputText={`${this.state.selectedRecommendedActionReasons.length} of ${this.state.recommendedActionReasons.length} options selected`}
          deselectAllOnSearch={true}
          items={this.state.recommendedActionReasons.map(model => ({ key: model.reason, name: model.friendlyName }))}
          label=""
          onSelectionChanged={this.handleRecommendedActionReasonDropdownChange}
          selectAllAppliesToAllItems={true}
          selectedItems={this.state.selectedRecommendedActionReasons.map((x): DropdownItem<string> => ({ key: x, name: null }))}
          testId="recommendedActionReasons-multiselect-test-id"
          disabled={this.state.recommendedActionReasonsFilterDisabled}
        />
      </>
    );
  }

  private renderPcaFilter() {
    const isExcluded = this.props.accuracyFilters?.excludePca;
    return (!isExcluded &&
      <AccuracyPcaFilter
        onPcaChange={this.handlePcaChange}
        onPcaChangeDropdown={this.handlePcaChangeDropdown}
        pcaIds={this.state.pcaLabelIds}
      />
    );
  }

  private renderRowTypesFilter() {
    return (
      <AccuracyRowTypeFilter
        onRowTypeChange={this.handleRowTypeChange}
        onRowTypeChangeDropdown={this.handleRowTypeChangeDropdown}
        rowTypeIds={this.state.rowTypeIds}
      />
    );
  }

  private renderSelectFormat() {
    return (
      <>
        <SelectFormat
          excelDisabled={this.props.exportNetworkProviderCount > this.state.excelRecordsLimit}
          exportType="actionable-providers"
          outputFormat={this.state.outputFormat}
          onOutputFormatChange={this.handleOutputFormatChange} />
        {this.props.exportNetworkProviderCount > this.state.excelRecordsLimit && <p className="text-danger"><i>Results are too large for Microsoft Excel Workbook (.xlsx).</i></p>}
      </>
    );
  }

  private renderTimeframeFilter() {
    return (
      <>
        <AccuracySelectComparisonRunConfigurationsFilter
          alwaysShowFilters={false}
          canCompareResults={this.state.canCompareResults}
          comparedProjectRunConfigId={this.state.comparedProjectRunConfigurationId}
          exportType="actionable-providers"
          handleCompareToggleChange={this.handleCompareToggleChange}
          isComparingResults={this.state.isComparingResults}
          onSourceRunConfigurationIdChange={this.handleSourceRunConfigurationIdChange}
          onComparedRunConfigurationIdChange={this.handleComparedRunConfigurationIdChange}
          runConfigurations={this.state.projectRunConfigurations}
          sourceProjectRunConfigId={this.state.sourceProjectRunConfigurationId}
        />
      </>
    );
  }

  private isViewByInspection() : boolean {
    return this.props.inspectionUids.length > 0 && this.props.inspectionBand == "Unknown"
  }

  private isViewByInspectionWithBand() : boolean {
    return this.props.inspectionUids.length == 1 && this.props.inspectionBand != "Unknown"
  }

  private isInspectionBandInvalidOrSuspect() : boolean {
    return this.props.inspectionBand == "Inaccurate" || this.props.inspectionBand == "Suspect";
  }

  private setDefaultShowAllRecommendedActionReasons() : boolean {
    return this.isInspectionBandInvalidOrSuspect() || this.isViewByInspection();
  }

  private setDefaultShowAllRecommendedActionReasonsDisabled() : boolean {
    return this.isViewByInspection() ||
            (this.isViewByInspectionWithBand() && this.isInspectionBandInvalidOrSuspect());
  }

  private setDefaultRecommendedActionReasonsFilterDisabled() {
    return this.isViewByInspectionWithBand()
            && !(this.isInspectionBandInvalidOrSuspect());
  }

  private setDefaultRecommendedActionIds() {
    const defaults: number[] = [];
    Object.values(RecommendedActionTypeEnum).map((v, i) => {
      if (v !== RecommendedActionTypeEnum.Unknown) {
        defaults.push(i);
      }
    });

    return defaults;
  }

  private handleTrueCountyChange = (value: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      filterByTrueCounty: value,
      reportSettingsHasChanges: true,
    }));
  }

  private setDefaultRowTypeIds() {
    const defaults: number[] = [];
    Object.values(AccuracySupplementalRowTypes).map((v, i) => {
      if (v !== AccuracySupplementalRowTypes.Undefined && v !== AccuracySupplementalRowTypes.Unknown) {
        defaults.push(i);
      }
    });

    return defaults;
  }

  private setDefaultPractitionerAtLocationConfidenceIds() {
    const defaults: number[] = [];
    Object.values(LocationConfidenceScoresLabelEnum).map((_, i) => {
      defaults.push(i);
    });

    return defaults;
  }

  private setDefaultPcaLabelIds() {
      const defaults: number[] = [];
      Object.values(PatientVolumeLabelEnum).map((_, i) => {
          defaults.push(i);
      });

      return defaults;
  }

  private setDefaultExcelRecordsLimit() {
    const defaultRowTypeIds: number[] = this.setDefaultRowTypeIds();
    return this.calculateExcelRecordsLimit(defaultRowTypeIds.length);
  }

  private handleShowAllReasonsChange = () => {
    const newValue = !this.state.showAllRecommendedActionReasons;
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasChanges: true,
      showAllRecommendedActionReasons: newValue
    }));
  }

  private handleRecommendedActionReasonChange = (recommendedActionReason: string) => {
    const selectedReasons = [...this.state.selectedRecommendedActionReasons];
    const index = selectedReasons.indexOf(recommendedActionReason);

    if (index > -1) {
      selectedReasons.splice(index, 1);
    } else {
      selectedReasons.push(recommendedActionReason);
    }

    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasChanges: true,
      selectAllRecommendedActionReasonChecked: (selectedReasons.length === this.state.recommendedActionReasons.length),
      selectAllRecommendedActionReasonIndeterminate: (selectedReasons.length > 0 && selectedReasons.length < this.state.recommendedActionReasons.length),
      selectedRecommendedActionReasons: selectedReasons
    }));
  }

  private handleRecommendedActionReasonDropdownChange = (recommendedActionReasons: Array<DropdownItem<string>>) => {
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasChanges: true,
      selectedRecommendedActionReasons: recommendedActionReasons.map(item => item.key)
    }));
  }

  private handleSelectedRecommendedActionReasons = (reportSettingsJson: string) => {
    const settings = helpers.tryParseReportSettings(reportSettingsJson);
    if (settings) {
      const selectedRecommendedActionReasons = (settings["selectedRecommendedActionReasons"] as string[]).filter(rar => this.state.recommendedActionReasons.map(r => r.reason).includes(rar));
      this.setState((prevState) => ({
        ...prevState,
        selectedRecommendedActionReasons,
      }));
    }
  }

  private handleSelectAllReasonsChecked = () => {
    const selectedReasons: string[] = [];

    this.state.recommendedActionReasons.forEach((r) => selectedReasons.push(r.reason));

    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasChanges: true,
      selectAllRecommendedActionReasonChecked: true,
      selectAllRecommendedActionReasonIndeterminate: (selectedReasons.length > 0 && selectedReasons.length < this.state.recommendedActionReasons.length),
      selectedRecommendedActionReasons: selectedReasons,
    }));
  }

  private handleSelectAllReasonsUnchecked = () => {
    this.setState((prevState): ActionableProvidersPanelBodyState => ({
      ...prevState,
      reportSettingsHasChanges: true,
      selectAllRecommendedActionReasonChecked: false,
      selectAllRecommendedActionReasonIndeterminate: false,
      selectedRecommendedActionReasons: [],
    }));
  }

  private handleProviderFilterTypeChange = (networkType: ProviderNetworkTypeEnum) => {
    const newName = helpers.reportName(startCase(networkType));
    this.setState((prevState) => ({
      ...prevState,
      providerFilter: {
        ...prevState.providerFilter,
        filterType: networkType,
      },
      reportName: newName,
      reportSettingsHasChanges: true,
    }));
  }

  private handleOutputFormatChange = (outputFormat: ExportFormatType) => {
    this.setState({ outputFormat, reportSettingsHasChanges: true, });
  }

  private handleCompareToggleChange = (active: boolean) => {
    const newName = helpers.reportName(active ? "ChangedProviders" : startCase(this.props.activeNetworkType || ProviderNetworkTypeEnum.Network));
    this.setState((prevState) => ({
      ...prevState,
      isComparingResults: active,
      reportName: newName,
      reportSettingsHasChanges: true,
    }));
  }

  private handleSourceRunConfigurationIdChange = (sourceRunConfigId: number) => {
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasChanges: true,
      sourceProjectRunConfigurationId: sourceRunConfigId
    }));
  }

  private handleComparedRunConfigurationIdChange = (comparedRunConfigId: number) => {
    this.setState((prevState) => ({
      ...prevState,
      comparedProjectRunConfigurationId: comparedRunConfigId,
      reportSettingsHasChanges: true,
    }));
  }

  private handleRecommendedActionChange = (id: number) => {
    const currentRecommendedActionIds = [...this.state.recommendedActionIds];

    if (currentRecommendedActionIds.indexOf(id) > -1) {
      currentRecommendedActionIds.splice(currentRecommendedActionIds.indexOf(id), 1);
    } else {
      currentRecommendedActionIds.push(id);
    }

    this.setState((prevState) => ({
      ...prevState,
      recommendedActionIds: currentRecommendedActionIds,
      reportSettingsHasChanges: true,
    }));
  }

  private handleRecommendedActionChangeDropdown = (ids: number[]) => {
    this.setState((prevState) => ({
      ...prevState,
      recommendedActionIds: ids,
      reportSettingsHasChanges: true,
    }));
  }

  private handlePracAtLocConfidenceChange = (confidenceScore: number) => {
    const currentPracAtLocConfidenceIds = [...this.state.practitionerAtLocationConfidenceIds];

    if (currentPracAtLocConfidenceIds.indexOf(confidenceScore) > -1) {
      currentPracAtLocConfidenceIds.splice(currentPracAtLocConfidenceIds.indexOf(confidenceScore), 1);
    } else {
      currentPracAtLocConfidenceIds.push(confidenceScore);
    }

    this.setState((prevState) => ({
      ...prevState,
      practitionerAtLocationConfidenceIds: currentPracAtLocConfidenceIds,
      reportSettingsHasChanges: true
    }));
  }

  private handlePracAtLocConfidenceChangeDropdown = (selectedItems: DropdownItem<number>[]) => {
    const ids = selectedItems.map((item) => item.key);

    this.setState((prevState) => ({
      ...prevState,
      practitionerAtLocationConfidenceIds: ids,
      reportSettingsHasChanges: true
    }));
  }

  private handlePcaChange = (pcaLabel: number) => {
    const currentPcaIds = [...this.state.pcaLabelIds];

    if (currentPcaIds.indexOf(pcaLabel) > -1) {
      currentPcaIds.splice(currentPcaIds.indexOf(pcaLabel), 1);
    } else {
      currentPcaIds.push(pcaLabel);
    }

    this.setState((prevState) => ({
      ...prevState,
      pcaLabelIds: currentPcaIds,
      reportSettingsHasChanges: true
    }));
  }

  private handlePcaChangeDropdown = (selectedItems: DropdownItem<number>[]) => {
    const ids = selectedItems.map((item) => item.key);

    this.setState((prevState) => ({
      ...prevState,
      pcaLabelIds: ids,
      reportSettingsHasChanges: true
    }));
  }

  private handleRowTypeChange = (id: number) => {
    const currentRowTypeIds = [...this.state.rowTypeIds];

    if (currentRowTypeIds.indexOf(id) > -1) {
      currentRowTypeIds.splice(currentRowTypeIds.indexOf(id), 1);
    } else {
      currentRowTypeIds.push(id);
    }

    this.setState((prevState) => ({
      ...prevState,
      excelRecordsLimit: this.calculateExcelRecordsLimit(currentRowTypeIds.length),
      reportSettingsHasChanges: true,
      rowTypeIds: currentRowTypeIds,
    }));
  }

  private handleRowTypeChangeDropdown = (selectedItems: DropdownItem<number>[]) => {
    const ids = selectedItems.map((item) => item.key);

    this.setState((prevState) => ({
      ...prevState,
      excelRecordsLimit: this.calculateExcelRecordsLimit(ids.length),
      reportSettingsHasChanges: true,
      rowTypeIds: ids,
    }));
  }

  private handleGenerateClick = async () => {
    const stateReportSettingsJSON = helpers.stringifyStateReportSettings(this.state);
    this.setState((prevState) => ({
      ...prevState,
      loadingReportSettingsJSONStrings: [
        ...prevState.loadingReportSettingsJSONStrings,
        stateReportSettingsJSON,
      ],
    }));

    switch (this.props.match.params.module) {
      case "accuracy":
        await this.generateAccuracyActionableProvidersExport();
        break;
      default:
        break;
    }

    toast.toastInfo(this.state.reportName);
  }

  // Accuracy
  private generateAccuracyActionableProvidersExport = async () => {
    const actionableProvidersModel: ExportAccuracyActionableProvidersModel = {
      comparedProjectRunConfigurationId: this.state.canCompareResults && this.state.isComparingResults ? this.state.comparedProjectRunConfigurationId : null,
      countyFilters: this.props.countyFilters,
      customProjectId: this.props.projectId,
      eMailOnSuccess: this.state.emailOnSuccess,
      exportName: this.state.reportName,
      filterByTrueCounty: this.state.filterByTrueCounty,
      formatType: this.state.outputFormat,
      includeActiveRegionSets: this.state.regionSetFilter.includeActive,
      includePendingRegionSets: this.state.regionSetFilter.includePending,
      includePractitionerEntityType: this.state.entityTypeFilter.includePractitionerEntityType,
      includeServiceLocationEntityType: this.state.entityTypeFilter.includeServiceLocationEntityType,
      inspectionBand: this.props.inspectionBand,
      inspectionUids: this.props.inspectionUids,
      networkAssessmentAnalysisType: this.state.regionSetFilter.networkAssessmentAnalysisType,
      pcaLabels: this.state.pcaLabelIds,
      practitionerAtLocationConfidenceScores: this.state.practitionerAtLocationConfidenceIds,
      projectRunConfigurationId: this.state.canCompareResults && this.state.isComparingResults ? this.state.sourceProjectRunConfigurationId : this.props.runConfigurationId,
      projectURL: this.props.projectURL,
      providerIds: this.props.providerIds,
      providerNetworkType: this.state.providerFilter.filterType,
      recommendedActionReasons: this.state.selectedRecommendedActionReasons,
      recommendedActions: this.state.recommendedActionIds,
      rowTypes: this.state.rowTypeIds,
      runAsynchronous: true,
      sharedUsers: this.state.sharedUsers,
      showAllRecommendedActionReasons: this.state.showAllRecommendedActionReasons,
      specialtyGroupCodes: this.props.specialtyGroupCodes,
      stateFilters: this.props.stateFilters,
    };

    await this.exportService.postExportAccuracyActionableProviders(actionableProvidersModel);
  }

  private handleEmailOnSuccessChange = (emailOnSuccess: boolean) => {
    this.setState({ emailOnSuccess, reportSettingsHasChanges: true });
  }

  private handleReportNameChange = (reportName: string) => {
    this.setState({ reportName });
  }

  private handleEntityTypeIncludeChange = (selected: boolean, isPractitioner: boolean) => {
    let practitionerSelected: boolean = this.state.entityTypeFilter.includePractitionerEntityType;
    let serviceLocationSelected: boolean = this.state.entityTypeFilter.includeServiceLocationEntityType;

    if (isPractitioner) {
      practitionerSelected = selected;
    } else {
      serviceLocationSelected = selected;
    }

    this.setState((prevState) => ({
      ...prevState,
      entityTypeFilter: {
        ...prevState.entityTypeFilter,
        includePractitionerEntityType: practitionerSelected,
        includeServiceLocationEntityType: serviceLocationSelected,
      },
      reportSettingsHasChanges: true
    }));
  }

  private handleEntityTypeIncludeChangeDropdown = (selectedItems: DropdownItem<string>[]) => {
    const practitionerSelected = !!selectedItems.find((item) => item.key === "practitioner");
    const serviceLocationSelected = !!selectedItems.find((item) => item.key === "servicelocation");

    this.setState((prevState) => ({
      ...prevState,
      entityTypeFilter: {
        includePractitionerEntityType: practitionerSelected,
        includeServiceLocationEntityType: serviceLocationSelected,
      },
      reportSettingsHasChanges: true
    }));
  }

  private calculateExcelRecordsLimit = (rowTypeIdQty: number) => {
    // Ex1: 1,000,000 max with 5 row type filters selected minus 50 non-provider records rounded down returns 199,950 row limit
    // Ex2: 1,000,000 max with 3 row type filters selected minus 50 non-provider records rounded down returns 333,283 row limit
    if (rowTypeIdQty > 0) {
      return Math.floor((this.props.maxExcelRecords / rowTypeIdQty) - this.props.nonRecordRows);
    }
    // Ex3: if no row type filters selected the max limit minus 50 non-provider records (999,950) is returned
    return this.props.maxExcelRecords - this.props.nonRecordRows;
  }

  // Report Settings
  private stringifyStateReportSettings = (): string => {
    return helpers.stringifyStateReportSettings({
      canCompareResults: this.state.canCompareResults,
      comparedProjectRunConfigurationId: this.state.comparedProjectRunConfigurationId,
      emailOnSuccess: this.state.emailOnSuccess,
      entityTypeFilter: this.state.entityTypeFilter,
      excelRecordsLimit: this.state.excelRecordsLimit,
      filterByTrueCounty: this.state.filterByTrueCounty,
      isComparingResults: this.state.isComparingResults,
      loadingReportSettingsJSONStrings: this.state.loadingReportSettingsJSONStrings,
      networkProviderFilterType: this.state.networkProviderFilterType,
      outputFormat: this.state.outputFormat,
      pcaLabelIds: this.state.pcaLabelIds,
      practitionerAtLocationConfidenceIds: this.state.practitionerAtLocationConfidenceIds,
      providerFilter: this.state.providerFilter,
      recommendedActionIds: this.state.recommendedActionIds,
      recommendedActionReasons: this.state.recommendedActionReasons,
      regionSetFilter: this.state.regionSetFilter,
      rowTypeIds: this.state.rowTypeIds,
      selectAllRecommendedActionReasonChecked: this.state.selectAllRecommendedActionReasonChecked,
      selectAllRecommendedActionReasonIndeterminate: this.state.selectAllRecommendedActionReasonIndeterminate,
      selectedRecommendedActionReasons: this.state.selectedRecommendedActionReasons,
      sharedUsers: this.state.sharedUsers,
      showAllRecommendedActionReasons: this.state.showAllRecommendedActionReasons,
      sourceProjectRunConfigurationId: this.state.sourceProjectRunConfigurationId,
    } as ActionableProvidersPanelBodyState);
  }

  private buildReportSettingsModel = (isDefault: boolean, name: string): ReportSettingsModel => {
    const reportSettingsJson = this.stringifyStateReportSettings();
    return {
      isDefault,
      name,
      reportSettingsJson,
      type: this.reportSettingsType,
    } as ReportSettingsModel;
  }

  private get reportSettingsType() {
    switch (this.props.match.params.module) {
      case "accuracy":
        return ReportSettingsType.AccuracyActionableProviders;
      default:
        return ReportSettingsType.Unknown;
    }
  }

  private handleParseReportSettingsJsonToState = (reportSettingsJson: string) => {
    const settings = helpers.tryParseReportSettings(reportSettingsJson);

    if (settings) {
      delete settings["recommendedActionReasons"];
      delete settings["regionSetFilter"]; // regionSetFilter options were removed from this report type, so will always be the default
      delete settings["projectRunConfigurations"]; // server-fetched runtime data; stale saved values would hide recent project runs from the comparison dropdown
      const currentRecommendedActionReasons = this.state.recommendedActionReasons.map(a => a.reason);
      settings["selectedRecommendedActionReasons"] = (settings["selectedRecommendedActionReasons"] as string[]).filter((selected) => currentRecommendedActionReasons.indexOf(selected) !== -1);
      settings["showAllRecommendedActionReasons"] = this.setDefaultShowAllRecommendedActionReasons() || settings["showAllRecommendedActionReasons"];
      this.setReportSettingsIsSelectionValid(true);
      this.setState((prevState) => ({
        ...prevState,
        ...settings,
      }));
    }
    else {
      const initialBodyState = this.getInitialBodyState();
      this.setReportSettingsIsSelectionValid(false);
      this.setState((prevState) => ({
        ...prevState,
        ...initialBodyState
      }));
    }
  }

  private setCurrentReportSettings = (currentReportSettings: ReportSettingsModel) => {
    this.setState((prevState) => ({
      ...prevState,
      currentReportSettings,
    }));
  }

  private setReportSettingsList = (rsList: ReportSettingsModel[]) => {
    const reportSettingsList = this.props.sortedReportSettings(rsList, "name");
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsList,
    }))
  }

  private setReportSettingsHasChanges = (reportSettingsHasChanges: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasChanges,
    }))
  }

  private setReportSettingsHasInvalidChanges = (reportSettingsHasInvalidChanges: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasInvalidChanges,
    }))
  }

  private setReportSettingsIsSelectionValid = (isSelectionValid: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      isSelectionValid,
    }))
  }

  private setCreateReportSettingsNameInputDisabled = (createReportSettingsNameInputDisabled: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      createReportSettingsNameInputDisabled,
    }))
  }

  private setSaveClicked = (saveClicked: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      saveClicked,
    }))
  }

  private handleSharedUsersChange = (sharedUsers: number[]) => {
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasChanges: true,
      sharedUsers: sharedUsers,
    }));
  }
}

const WrappedActionableProvidersPanelBody = withFeatureFlagContext(withUserContext(ActionableProvidersPanelBody));

export { WrappedActionableProvidersPanelBody as ActionableProvidersPanelBody };